<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Select from 'primevue/select';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import ProgressSpinner from 'primevue/progressspinner';
import UserAvatar from '@/components/UserAvatar.vue';
import FloatingBonusLayer from '@/components/FloatingBonusLayer.vue';
import type { PlaygroundCategory, PlaygroundLeader } from '@/types/api';
import { PLAYGROUND_CATEGORIES } from '@/types/api';
import { fetchPlaygroundLeaders, fetchPlaygroundScores } from '@/lib/apiClient';
import { useAuth } from '@/composables/useAuth';
import { usePreventPageZoom } from '@/composables/usePreventPageZoom';
import { useFloatingBonus } from '@/composables/useFloatingBonus';
import { usePlaygroundPending } from '@/composables/usePlaygroundPending';
import { useBonusLoot } from '@/composables/useBonusLoot';

const router = useRouter();
const toast = useToast();
const { token, user } = useAuth();

const loading = ref(true);
const saving = ref(false);
const leadersLoading = ref(false);
const activeTab = ref(0);
const selectedCategory = ref<PlaygroundCategory | null>(null);
const leaders = ref<PlaygroundLeader[]>([]);
const bump = ref(false);
const suppressClickUntil = ref(0);
const arenaRef = ref<HTMLElement | null>(null);

const userId = computed(() => user.value?.id);
const {
  displayScores,
  hasPending,
  setServerScores,
  addClick,
  addBonus,
  flushToServer,
} = usePlaygroundPending(userId);

const { roll: rollBonus } = useBonusLoot();

const clickerActive = computed(() => activeTab.value === 0 && selectedCategory.value !== null);
usePreventPageZoom(clickerActive);

const { targets: bonusTargets, handleTargetClick: handleBonusTargetClick } = useFloatingBonus({
  active: clickerActive,
  containerRef: arenaRef,
  onBonus: async () => {
    if (!selectedCategory.value) return;
    const amount = await rollBonus();
    if (amount <= 0) {
      toast.add({
        severity: 'info',
        summary: 'Мимо!',
        detail: 'В этот раз бонус не выпал',
        life: 1200,
      });
      return;
    }
    addBonus(selectedCategory.value, amount);
    toast.add({
      severity: 'success',
      summary: `Бонус +${amount}!`,
      detail: 'Очки добавятся при сохранении',
      life: 1800,
    });
  },
});

const categoryOptions = Object.entries(PLAYGROUND_CATEGORIES).map(([value, meta]) => ({
  label: `${meta.emoji} ${meta.label}`,
  value: value as PlaygroundCategory,
}));

const activeMeta = computed(() =>
  selectedCategory.value ? PLAYGROUND_CATEGORIES[selectedCategory.value] : null,
);

const currentCategoryScore = computed(() => {
  if (!selectedCategory.value) return 0;
  return displayScores.value[selectedCategory.value];
});

function rankMedal(rank: number) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return rank;
}

async function loadScores() {
  if (!token.value) return;
  setServerScores((await fetchPlaygroundScores(token.value)).scores);
}

async function loadLeaders() {
  if (!token.value) return;
  leadersLoading.value = true;
  try {
    leaders.value = (await fetchPlaygroundLeaders(token.value)).leaders;
  } finally {
    leadersLoading.value = false;
  }
}

onMounted(async () => {
  try {
    await loadScores();
  } finally {
    loading.value = false;
  }
});

function onTabChange(event: { index: number }) {
  if (event.index === 1 && leaders.value.length === 0) {
    loadLeaders();
  }
}

function handleClick() {
  if (!selectedCategory.value) return;

  addClick(selectedCategory.value);

  bump.value = true;
  window.setTimeout(() => {
    bump.value = false;
  }, 130);
}

async function handleSave() {
  if (!token.value || !hasPending.value) return;

  saving.value = true;
  try {
    await flushToServer(token.value);
    toast.add({
      severity: 'success',
      summary: 'Сохранено на сервер!',
      detail: 'Клики и бонусные очки отправлены — можно забирать их в квесте',
      life: 3500,
    });
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Не удалось сохранить',
      detail: error instanceof Error ? error.message : 'Ошибка сервера',
      life: 3000,
    });
  } finally {
    saving.value = false;
  }
}

function handlePointerDown(event: PointerEvent) {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  if (Date.now() < suppressClickUntil.value) {
    event.preventDefault();
    return;
  }
  handleClick();
}

function handleButtonTouchStart(event: TouchEvent) {
  if (!selectedCategory.value || event.touches.length !== 1) return;
  event.preventDefault();
  suppressClickUntil.value = Date.now() + 700;
  handleClick();
}
</script>

<template>
  <div class="page">
    <Button
      label="К играм"
      icon="pi pi-arrow-left"
      severity="secondary"
      text
      @click="router.push({ name: 'playground' })"
    />

    <section class="page__hero">
      <span class="page__badge">✨ Три Желания</span>
      <h1>Кликай — копи удачу</h1>
      <p>Клики копятся локально. Лови ✨ цели — выпадет случайный бонус из таблицы. Сохраняй, когда закончишь!</p>
    </section>

    <ProgressSpinner v-if="loading" />

    <TabView v-else v-model:active-index="activeTab" class="playground-tabs" @tab-change="onTabChange">
      <TabPanel value="play" header="Поиграть">
        <div class="play">
          <div class="play__stats">
            <div class="play__stat">
              <span>💰</span>
              <strong>{{ displayScores.money }}</strong>
              <small>Деньги</small>
            </div>
            <div class="play__stat">
              <span>💚</span>
              <strong>{{ displayScores.health }}</strong>
              <small>Здоровье</small>
            </div>
            <div class="play__stat">
              <span>❤️</span>
              <strong>{{ displayScores.love }}</strong>
              <small>Любовь</small>
            </div>
            <div class="play__stat play__stat--total">
              <span>🏆</span>
              <strong>{{ displayScores.total }}</strong>
              <small>Всего</small>
            </div>
          </div>

          <label class="play__select">
            <span>Выберите категорию</span>
            <Select
              v-model="selectedCategory"
              :options="categoryOptions"
              option-label="label"
              option-value="value"
              placeholder="Деньги, здоровье или любовь..."
              fluid
            />
          </label>

          <div
            v-if="selectedCategory && activeMeta"
            ref="arenaRef"
            class="play__arena"
          >
            <FloatingBonusLayer :targets="bonusTargets" @hit="handleBonusTargetClick" />
            <p class="play__hint">Жми быстро — лови ✨ цели. Сохраняй, когда закончишь!</p>
            <p class="play__counter">{{ currentCategoryScore }}</p>
            <div
              role="button"
              tabindex="0"
              class="play__button"
              :class="{ 'play__button--bump': bump }"
              :style="{ background: activeMeta.gradient, borderColor: activeMeta.color }"
              @pointerdown="handlePointerDown"
              @touchstart="handleButtonTouchStart"
              @keydown.enter.prevent="handleClick"
              @keydown.space.prevent="handleClick"
            >
              <span class="play__button-emoji">{{ activeMeta.emoji }}</span>
              <span class="play__button-label">{{ activeMeta.label }}</span>
            </div>
            <Button
              label="Сохранить на сервер"
              icon="pi pi-save"
              class="play__save"
              :loading="saving"
              :disabled="!hasPending"
              @click="handleSave"
            />
          </div>
        </div>
      </TabPanel>

      <TabPanel value="champions" header="Чемпионы">
        <div class="champions">
          <div class="champions__toolbar">
            <p>Рейтинг по сумме кликов: деньги + здоровье + любовь</p>
            <button type="button" class="champions__refresh" @click="loadLeaders">Обновить</button>
          </div>

          <ProgressSpinner v-if="leadersLoading" />

          <DataTable
            v-else
            :value="leaders"
            striped-rows
            class="champions__table"
            :row-class="(data) => (data.userId === user?.id ? 'champions__row--me' : '')"
          >
            <Column header="Место" style="width: 5rem">
              <template #body="{ data }">
                <span class="champions__rank">{{ rankMedal(data.rank) }}</span>
              </template>
            </Column>
            <Column header="Игрок">
              <template #body="{ data }">
                <div class="champions__player">
                  <UserAvatar :nickname="data.nickname" :avatar="data.avatar" />
                  <strong>{{ data.nickname }}</strong>
                </div>
              </template>
            </Column>
            <Column header="💰 Деньги" field="money" style="width: 7rem; text-align: center" />
            <Column header="💚 Здоровье" field="health" style="width: 7rem; text-align: center" />
            <Column header="❤️ Любовь" field="love" style="width: 7rem; text-align: center" />
            <Column header="Σ Всего" field="total" style="width: 7rem; text-align: center">
              <template #body="{ data }">
                <strong class="champions__total">{{ data.total }}</strong>
              </template>
            </Column>
          </DataTable>

          <p v-if="!leadersLoading && leaders.length === 0" class="champions__empty">
            Пока никто не кликал — будь первым на вкладке «Поиграть»!
          </p>
        </div>
      </TabPanel>
    </TabView>
  </div>
</template>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: 920px;
  padding-bottom: 1rem;
}

.playground-tabs {
  width: 100%;
}

.playground-tabs :deep(.p-tabview) {
  display: flex;
  flex-direction: column;
}

.playground-tabs :deep(.p-tabview-nav) {
  background: transparent;
  border: none;
  flex-shrink: 0;
}

.playground-tabs :deep(.p-tabview-panels) {
  background: transparent;
  padding: 1.25rem 0 0;
  overflow: visible;
}

.playground-tabs :deep(.p-tabview-panel) {
  overflow: visible;
}

.page__hero h1 {
  margin: 0.35rem 0;
  color: #831843;
}

.page__badge {
  display: inline-block;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: #fce7f3;
  color: #be185d;
  font-weight: 700;
  font-size: 0.85rem;
}

.page__hero p {
  margin: 0;
  color: #9d174d;
}

.play__stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.play__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.85rem;
  border-radius: 1rem;
  background: #fff7fb;
  border: 1px solid #fbcfe8;
}

.play__stat span:first-child {
  font-size: 1.4rem;
}

.play__stat strong {
  font-size: 1.35rem;
  color: #db2777;
}

.play__stat small {
  color: #9d174d;
  font-weight: 600;
}

.play__stat--total {
  background: linear-gradient(135deg, #fff1f2, #fdf2f8);
  border-color: #f9a8d4;
}

.play__select {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  color: #831843;
  font-weight: 600;
}

.play__arena {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 1rem 2.5rem;
  border-radius: 1.5rem;
  background: linear-gradient(180deg, #fff7fb, #fdf2f8);
  border: 2px dashed #fbcfe8;
  touch-action: pan-y;
  -webkit-user-select: none;
  user-select: none;
}

.play__hint {
  margin: 0;
  color: #be185d;
  font-weight: 600;
  text-align: center;
}

.play__counter {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  color: #831843;
}

.play__button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: min(280px, 80vw);
  height: min(280px, 80vw);
  border-radius: 50%;
  border: 4px solid;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.15s ease;
  box-shadow: 0 16px 40px rgb(219 39 119 / 18%);
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  outline: none;
}

.play__button:hover {
  transform: scale(1.02);
}

.play__button:active,
.play__button--bump {
  transform: scale(0.94);
  box-shadow: 0 8px 20px rgb(219 39 119 / 22%);
}

.play__button-emoji {
  font-size: clamp(4rem, 18vw, 5.5rem);
  line-height: 1;
}

.play__button-label {
  font-size: 1.25rem;
  font-weight: 800;
  color: #831843;
}

.play__save {
  width: min(280px, 100%);
  margin-top: 0.35rem;
}

.champions__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.champions__toolbar p {
  margin: 0;
  color: #9d174d;
}

.champions__refresh {
  border: none;
  border-radius: 999px;
  padding: 0.55rem 1rem;
  background: #fce7f3;
  color: #831843;
  font-weight: 700;
  cursor: pointer;
}

.champions__table :deep(.p-datatable-table) {
  border-radius: 1rem;
  overflow: hidden;
}

.champions__table :deep(.p-datatable-thead > tr > th) {
  background: #fdf2f8;
  color: #831843;
}

.champions__table :deep(.p-datatable-tbody > tr > td) {
  color: #9d174d;
}

.champions__table :deep(.champions__row--me) {
  background: #fff1f2 !important;
}

.champions__rank {
  font-size: 1.2rem;
  font-weight: 700;
}

.champions__player {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.champions__player strong {
  color: #831843;
}

.champions__total {
  color: #db2777;
  font-size: 1.05rem;
}

.champions__empty {
  text-align: center;
  color: #9d174d;
  padding: 2rem 0;
}

@media (max-width: 640px) {
  .page {
    padding-bottom: max(1rem, env(safe-area-inset-bottom, 0));
  }

  .play__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .play__button {
    width: min(240px, 72vw);
    height: min(240px, 72vw);
  }

  .champions__toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
